import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Truck } from "@phosphor-icons/react";
import { parseNum } from "@/lib/utils";

const EMPTY = {
  // Cavalo
  plate_truck: "", brand_truck: "", model: "", year: "", color_truck: "", drivetrain: "", power_hp: "",
  start_km_truck: "", start_date_truck: "",
  // Reboque 1
  plate_trailer1: "", brand_trailer1: "", model_trailer1: "", year_trailer1: "", color_trailer1: "", start_date_trailer1: "",
  // Dolly
  plate_dolly: "", brand_dolly: "", model_dolly: "", year_dolly: "", color_dolly: "", start_date_dolly: "",
  // Reboque 2
  plate_trailer2: "", brand_trailer2: "", model_trailer2: "", year_trailer2: "", color_trailer2: "", start_date_trailer2: "",
  // Conjunto
  category: "", vehicle_type: "", body_type: "", tara: "", capacity: "", axles: "", color: "",
};

const CATEGORIAS = [
  "3/4", "Truck", "Bi-truck", "LS Simples", "LS 4º eixo", "Bitrem", "Rodotrem", "Outro",
];
const DRIVETRAIN_OPTIONS = ["Simples (4x2)", "Trucado (6x2)", "Traçado (6x4)", "8x2", "8x4"];

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmPwd, setConfirmPwd] = useState("");

  const load = async () => { const { data } = await api.get("/vehicles"); setItems(data); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail?.entity === "vehicle") load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const NUM_FIELDS = ["tara", "capacity", "axles", "start_km_truck"];

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      NUM_FIELDS.forEach((k) => { payload[k] = parseNum(payload[k]); });
      if (editing) {
        await api.post("/auth/confirm-password", { password: confirmPwd });
        await api.put(`/vehicles/${editing.id}`, payload);
        toast.success("Veículo atualizado");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Veículo cadastrado");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); setConfirmPwd(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir veículo?")) return;
    await api.delete(`/vehicles/${id}`); toast.success("Removido"); load();
  };

  const startEdit = (v) => {
    setEditing(v);
    // Preenche defaults faltantes vindo do backend (compatibilidade com veículos antigos)
    setForm({ ...EMPTY, ...v, start_km_truck: v.start_km_truck || "", start_date_truck: v.start_date_truck || "" });
    setOpen(true);
  };
  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  const Section = ({ title, color = "blue" }) => (
    <div className={`col-span-full text-xs font-bold uppercase tracking-wider text-${color}-700 border-b-2 border-${color}-200 pb-1 mt-3 flex items-center gap-2`}>
      <Truck size={14} weight="duotone" /> {title}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <Truck size={32} weight="duotone" className="text-blue-600" /> Veículos
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Cadastre o cavalo, implementos e classifique o conjunto</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startNew} data-testid="btn-new-vehicle" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Plus size={16} /> Novo Veículo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} Veículo / Conjunto</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* ==================== CAVALO ==================== */}
              <Section title="🚛 Cavalo mecânico (Caminhão)" />
              <div className="md:col-span-2"><Label>Placa Cavalo *</Label><Input data-testid="veh-plate" required value={form.plate_truck} onChange={set("plate_truck")} placeholder="ABC1D23" /></div>
              <div><Label>Marca</Label><Input value={form.brand_truck} onChange={set("brand_truck")} placeholder="Scania, Volvo, MB..." /></div>
              <div><Label>Modelo</Label><Input value={form.model} onChange={set("model")} placeholder="R450, FH460..." /></div>
              <div><Label>Ano</Label><Input value={form.year} onChange={set("year")} placeholder="2020" /></div>
              <div><Label>Cor</Label><Input value={form.color_truck} onChange={set("color_truck")} placeholder="Branco" /></div>
              <div>
                <Label>Traçado</Label>
                <select data-testid="veh-drivetrain" className="w-full border-2 rounded-md h-10 px-2 bg-white text-sm" value={form.drivetrain} onChange={set("drivetrain")}>
                  <option value="">-- selecione --</option>
                  {DRIVETRAIN_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div><Label>Potência (cv)</Label><Input value={form.power_hp} onChange={set("power_hp")} placeholder="450" inputMode="numeric" /></div>
              <div><Label>KM inicial (no app)</Label><Input value={form.start_km_truck} onChange={set("start_km_truck")} placeholder="150000" inputMode="decimal" data-testid="veh-start-km" /></div>
              <div><Label>Data inicial</Label><Input type="date" value={form.start_date_truck} onChange={set("start_date_truck")} data-testid="veh-start-date" /></div>

              {/* ==================== REBOQUE 1 ==================== */}
              <Section title="📦 Reboque 1 (Carreta 1)" color="emerald" />
              <div className="md:col-span-2"><Label>Placa Reboque 1</Label><Input value={form.plate_trailer1} onChange={set("plate_trailer1")} /></div>
              <div><Label>Marca</Label><Input value={form.brand_trailer1} onChange={set("brand_trailer1")} placeholder="Librelato, Randon..." /></div>
              <div><Label>Modelo</Label><Input value={form.model_trailer1} onChange={set("model_trailer1")} /></div>
              <div><Label>Ano</Label><Input value={form.year_trailer1} onChange={set("year_trailer1")} /></div>
              <div><Label>Cor</Label><Input value={form.color_trailer1} onChange={set("color_trailer1")} /></div>
              <div><Label>Data inicial</Label><Input type="date" value={form.start_date_trailer1} onChange={set("start_date_trailer1")} /></div>

              {/* ==================== DOLLY ==================== */}
              <Section title="🔗 Dolly (opcional)" color="amber" />
              <div className="md:col-span-2"><Label>Placa Dolly</Label><Input value={form.plate_dolly} onChange={set("plate_dolly")} placeholder="Deixe em branco se não houver" /></div>
              <div><Label>Marca</Label><Input value={form.brand_dolly} onChange={set("brand_dolly")} /></div>
              <div><Label>Modelo</Label><Input value={form.model_dolly} onChange={set("model_dolly")} /></div>
              <div><Label>Ano</Label><Input value={form.year_dolly} onChange={set("year_dolly")} /></div>
              <div><Label>Cor</Label><Input value={form.color_dolly} onChange={set("color_dolly")} /></div>
              <div><Label>Data inicial</Label><Input type="date" value={form.start_date_dolly} onChange={set("start_date_dolly")} /></div>

              {/* ==================== REBOQUE 2 ==================== */}
              <Section title="📦 Reboque 2 (Carreta 2)" color="purple" />
              <div className="md:col-span-2"><Label>Placa Reboque 2</Label><Input value={form.plate_trailer2} onChange={set("plate_trailer2")} /></div>
              <div><Label>Marca</Label><Input value={form.brand_trailer2} onChange={set("brand_trailer2")} /></div>
              <div><Label>Modelo</Label><Input value={form.model_trailer2} onChange={set("model_trailer2")} /></div>
              <div><Label>Ano</Label><Input value={form.year_trailer2} onChange={set("year_trailer2")} /></div>
              <div><Label>Cor</Label><Input value={form.color_trailer2} onChange={set("color_trailer2")} /></div>
              <div><Label>Data inicial</Label><Input type="date" value={form.start_date_trailer2} onChange={set("start_date_trailer2")} /></div>

              {/* ==================== CONJUNTO (CLASSIFICAÇÃO) ==================== */}
              <Section title="🎯 Classificação do Conjunto" color="rose" />
              <div className="md:col-span-2">
                <Label>Categoria *</Label>
                <select data-testid="veh-category" required className="w-full border-2 rounded-md h-10 px-2 bg-white text-sm font-semibold" value={form.category} onChange={set("category")}>
                  <option value="">-- selecione a categoria --</option>
                  {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><Label>Carroceria</Label><Input value={form.body_type} onChange={set("body_type")} placeholder="Baú, Graneleiro, Sider..." /></div>
              <div><Label>Tara total (kg)</Label><Input type="text" inputMode="decimal" value={form.tara} onChange={set("tara")} data-testid="veh-tara" /></div>
              <div><Label>Capacidade total (kg)</Label><Input type="text" inputMode="decimal" value={form.capacity} onChange={set("capacity")} data-testid="veh-capacity" /></div>
              <div><Label>Eixos totais</Label><Input type="text" inputMode="numeric" value={form.axles} onChange={set("axles")} data-testid="veh-axles" /></div>

              {editing && (
                <div className="col-span-full border-t-2 pt-3 mt-2 bg-amber-50 -mx-2 px-3 pb-3 rounded-lg">
                  <Label className="text-amber-900 font-semibold">🔐 Confirme sua senha para salvar alterações</Label>
                  <Input type="password" data-testid="confirm-password" required value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} />
                </div>
              )}
              <DialogFooter className="col-span-full">
                <Button type="submit" data-testid="btn-save-vehicle" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">{editing ? "Salvar Alterações" : "Cadastrar"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Frota ({items.length})</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? <p className="text-muted-foreground text-sm">Nenhum veículo cadastrado.</p> : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader><TableRow><TableHead>Placa</TableHead><TableHead>Marca/Modelo</TableHead><TableHead>Categoria</TableHead><TableHead className="text-right">Capacidade</TableHead><TableHead>Eixos</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {items.map((v) => (
                      <TableRow key={v.id} data-testid={`vehicle-row-${v.plate_truck}`}>
                        <TableCell className="font-semibold">{v.plate_truck}</TableCell>
                        <TableCell>{[v.brand_truck, v.model].filter(Boolean).join(" ") || "-"}</TableCell>
                        <TableCell><span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">{v.category || "-"}</span></TableCell>
                        <TableCell className="text-right">{v.capacity ? `${v.capacity} kg` : "-"}</TableCell>
                        <TableCell>{v.axles || "-"}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`btn-edit-${v.plate_truck}`} className="border-blue-400 text-blue-700 hover:bg-blue-50 mr-1">
                            <PencilSimple size={14} className="mr-1" /> Editar
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(v.id)} data-testid={`btn-del-${v.plate_truck}`}><Trash size={16} className="text-red-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {items.map((v) => (
                  <div key={v.id} data-testid={`vehicle-card-${v.plate_truck}`} className="border-2 rounded-lg p-3 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-black text-lg">{v.plate_truck}</div>
                        <div className="text-sm text-slate-600">{[v.brand_truck, v.model].filter(Boolean).join(" ") || "-"}</div>
                        {v.category && <span className="inline-block px-2 py-0.5 mt-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">{v.category}</span>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button size="sm" variant="outline" onClick={() => startEdit(v)} data-testid={`btn-edit-m-${v.plate_truck}`} className="border-blue-400 text-blue-700 hover:bg-blue-50">
                          <PencilSimple size={14} className="mr-1" /> Editar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(v.id)} data-testid={`btn-del-m-${v.plate_truck}`}><Trash size={14} className="text-red-600 mr-1" /> Excluir</Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-slate-600">
                      {v.capacity ? <div>Cap.: <strong>{v.capacity} kg</strong></div> : null}
                      {v.axles ? <div>Eixos: <strong>{v.axles}</strong></div> : null}
                      {v.body_type ? <div>Carroc.: <strong>{v.body_type}</strong></div> : null}
                      {v.start_km_truck ? <div>KM início: <strong>{v.start_km_truck}</strong></div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
